import { useEffect } from 'react'
import { setCookie } from 'cookies-next'
import { IncomingMessage, ServerResponse } from 'http'
import { ParsedUrlQuery } from 'querystring'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { ServerExperiments } from '@/experimentation'
import {
  ExperimentParamsArray,
  runContentfulExperimentOnServer,
  runExperimentsOnServer,
} from '@/experimentation/server'
import { Seo, getBasicPageMetaData } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { TvAuthToast } from '@/molecules/TvAuthToast'
import { useLegacyEffect } from '@/molecules/utils'
import { GuildGeogatedBenefitsPrompt } from '@/organisms/GuildGeogatedBenefitsPrompt'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { getContentfulClient } from '@/services/ApolloClient'
import { GUILD_PREDICTS_WINNERS_ENTRY_ID, getCollection } from '@/services/CmsService/Collection'
import { CatalogTitle, fetchTitle } from '@/services/ContentCatalog'
import { Promotion, getGuildPromotion } from '@/services/GuildPromotionService'
import {
  getExplainerLayout,
  getGuildSignupPageBySlug,
  getTestimonialSection,
  getTicketsSection,
  GuildSignupPageData,
} from '@/services/GuildSignupPageService'
import {
  GuildProjectSlugs,
  GuildSignupEmphasis,
  GuildSignupLayout,
} from '@/services/GuildSignupPageService/GuildSignupPageTypes'
import { isGuildSignupSimpleLayout } from '@/services/GuildSignupPageService/validations'
import { getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import { GuildSale, getGuildSale } from '@/services/SaleService'
import {
  Collection,
  GuildSignupExplainerLayout,
  GuildSignupTicketsSection,
  GuildTestimonialSection,
} from '@/types/codegen-contentful'
import { useSafeTrack } from '@/utils/analytics'
import { useStickyAttribution } from '@/utils/analytics/StickyAttribution'
import { projectSlugsAttribution } from '@/utils/analytics/attribution'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { omitUndefineds } from '@/utils/object'
import { writeToSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { GuildStatus } from '@/utils/users/usersServer'
import { GuildSignupView } from '@/views/GuildSignupView'
import { GuildSignupContextProvider } from '@/views/GuildSignupView/GuildSignupContext'
import { getGuildApprovedTorchPosters } from '@/views/GuildSignupView/GuildSignupContext/GuildSignupContext'

interface GuildSignupProps {
  catalogTitle?: CatalogTitle
  emphasis: GuildSignupEmphasis
  guildMemberCta: string
  guildMemberHref: string
  guildApprovedTorchPosters: string[]
  guildExplainerLayout?: GuildSignupExplainerLayout | null
  layout: GuildSignupLayout
  locale: string
  projectSlugs?: GuildProjectSlugs
  promotion: Promotion | null
  sale: GuildSale | null
  slug: string
  votingPhoneImageUrl?: string | null
  guildTestimonialSection?: GuildTestimonialSection
  guildTicketsSection?: GuildSignupTicketsSection
  guildPredictsWinners?: Collection
  hasUser: boolean
  logo?: {
    src: string
    alt: string
    className?: string
  }
  experiments: ServerExperiments
  region: string | null
  isDarkMode?: boolean
}

export const GuildSignupPage: NextPageWithLayout<GuildSignupProps> = ({
  catalogTitle,
  emphasis,
  guildMemberCta,
  guildMemberHref,
  guildApprovedTorchPosters,
  guildExplainerLayout,
  layout,
  locale,
  projectSlugs,
  promotion,
  sale,
  slug,
  votingPhoneImageUrl,
  guildTestimonialSection,
  guildTicketsSection,
  guildPredictsWinners,
  hasUser,
  logo,
  experiments,
  region,
}) => {
  const { t } = useTranslate('guild')
  const track = useSafeTrack()

  useLegacyEffect(() => {
    track('Guild Product Page Viewed', { pageContext: emphasis, slug })
    setCookie('guildFocus', slug, { maxAge: Durations.ONE_YEAR_IN_SECONDS })
  }, [track, slug, emphasis])

  useEffect(() => {
    writeToSessionStorage('pageSlug', slug)
    writeToSessionStorage('guildCheckoutOrigin', `/guild/join/${slug}`)
    writeToSessionStorage('guildEcommerceProductId', slug)
    projectSlugsAttribution(projectSlugs)
  }, [slug, projectSlugs])

  useStickyAttribution('guild-attribution')

  const angelGuildPages = new Set(['angel-studios', 'sale', 'monthly', 'yearly', 'angel', 'angel-studios-test'])
  const showSocialProof = angelGuildPages.has(slug)

  return (
    <div className={isGuildSignupSimpleLayout(layout) ? 'bg-white' : 'bg-core-gray-950'}>
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: 'v1736351150/angel-studios/guild/amgel-guild-open-graph-241030.webp',
          cloudinaryTransformation: 'b_rgb:000000',
          description: t(
            'angelStudiosGuildJoinInfoMetaDescriptionV2',
            'As an Angel Guild member, you can vote on upcoming shows, receive free tickets to every theatrical release, gain early access to shows, receive merchandise discounts, and continue to amplify light.',
          ),
          locale,
          path: `${paths.guild.join}/${slug}`,
          title: t('angelGuildEarlyAccessMetaTitleV2', 'Angel Guild Membership | Angel'),
        })}
      />
      <GuildSignupContextProvider
        catalogTitle={catalogTitle}
        guildTestimonialSection={guildTestimonialSection}
        guildTicketsSection={guildTicketsSection}
        emphasis={emphasis}
        guildApprovedTorchPosters={guildApprovedTorchPosters}
        guildExplainerLayout={guildExplainerLayout}
        guildMemberCta={guildMemberCta}
        guildMemberHref={guildMemberHref}
        layout={layout}
        projectSlugs={projectSlugs}
        promotion={promotion}
        sale={sale}
        slug={slug}
        votingPhoneImageUrl={votingPhoneImageUrl}
        logo={logo}
        experiments={experiments}
        guildPredictsWinners={guildPredictsWinners}
        region={region}
        showSocialProof={showSocialProof}
      >
        <GuildSignupView hasUser={hasUser} />
      </GuildSignupContextProvider>
      <MarketingVideoPopup ctaHref={'#plan-selection'} ctaText={t('joinTheGuild', 'Join the Guild')} />
      <TvAuthToast />
      <GuildGeogatedBenefitsPrompt slug={slug} />
    </div>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ federationClient, locale, params, preview, query, req, res, userId, region }) => {
    const isSupportLink = query.special === 'last-chance'
    const slug = getItemOrFirstEntry(params?.slug)
    if (!slug) {
      return { redirect: { destination: paths.guild.join, permanent: false } }
    }

    const opts = { locale, preview }
    const contentfulClient = getContentfulClient({ preview, locale })
    const [pageData, guildStatus] = await Promise.all([
      getGuildSignupPageBySlug({ contentfulClient, slug, opts }),
      getGuildStatus(federationClient),
    ])

    // If Guild Member and redirected from home, push to /watch instead
    if (slug === 'sale' && query.origin && query.origin === 'home' && guildStatus?.isGuildMember) {
      return {
        redirect: {
          destination: '/watch',
          permanent: false,
        },
      }
    }

    if (!pageData) {
      return { redirect: { destination: paths.guild.join, permanent: false } }
    }
    if (pageData.redirectTo) {
      return { redirect: { destination: pageData.redirectTo, permanent: false } }
    }

    const emphasis = pageData.emphasis
    if (shouldRedirectToDashboard(guildStatus, emphasis)) {
      return {
        redirect: {
          destination: getDestinationUrl(pageData, emphasis, query),
          permanent: false,
        },
      }
    }

    const layoutExperiment = await runContentfulExperimentOnServer(
      req,
      res,
      pageData.layoutExperimentCollection?.items ?? [],
    )
    const layout = layoutExperiment ?? pageData.layout // need to support both layout and layoutExperiment for older pages

    const guildExplainerLayout = getExplainerLayout(pageData)
    const guildTestimonialSection = getTestimonialSection(pageData)
    const guildTicketsSection = getTicketsSection(pageData)

    const [guildApprovedTorchPosters, promotion, sale, guildPredictsWinners, experiments, catalogTitle, translations] =
      await Promise.all([
        getGuildApprovedTorchPosters(),
        getGuildPromotion({ opts: { locale, preview }, isSupportLink, federationClient }),
        getGuildSale({ opts: { preview, locale }, federationClient }),
        getCollection({ locale, preview }, GUILD_PREDICTS_WINNERS_ENTRY_ID),
        runExperiments({
          req,
          res,
          slug,
        }),
        fetchTitle<CatalogTitle>(locale, pageData?.project?.contentCatalogId),
        loadTranslations(locale, ['common', 'join-the-guild', 'guild', 'guild-benefits', 'app-promo', 'watch']),
      ])

    return {
      props: {
        catalogTitle: omitUndefineds(catalogTitle),
        emphasis,
        guildApprovedTorchPosters,
        guildExplainerLayout,
        guildMemberCta: pageData.guildMemberCta,
        guildMemberHref: pageData.guildMemberHref,
        layout,
        locale,
        projectSlugs: pageData.projectSlugs,
        promotion,
        sale,
        slug,
        votingPhoneImageUrl: pageData.votingPhoneImageUrl ?? null,
        logo: pageData?.project?.logo ?? null,
        guildTestimonialSection,
        guildTicketsSection,
        guildPredictsWinners,
        hasUser: Boolean(userId),
        experiments,
        isDarkMode: layout?.__typename !== 'GuildSignupSimpleLayout',
        region,
        ...translations,
      },
    }
  },
)

GuildSignupPage.getLayout = (page, pageProps) => {
  return <DefaultLayout isDarkMode={Boolean(pageProps?.isDarkMode)}>{page}</DefaultLayout>
}

export default GuildSignupPage

function shouldRedirectToDashboard(guildStatus: GuildStatus | null, emphasis: GuildSignupEmphasis) {
  if (!guildStatus) return false
  if (emphasis === 'tickets' && guildStatus.guildRoles?.hasFreeTickets) return true
  if (emphasis === 'early-access' && guildStatus.guildRoles?.hasEarlyAccess) return true
  return false
}

function getDestinationUrl(pageData: GuildSignupPageData, emphasis: GuildSignupEmphasis, query: ParsedUrlQuery) {
  let destination = pageData.guildMemberHref ?? `/guild#${emphasis}`
  if (query['tv-post-auth'] === 'True') {
    const hasQuery = destination.includes('?')
    destination = `${destination}${hasQuery ? '&' : '?'}tv-post-auth=True`
  }
  return destination
}

async function runExperiments({
  req,
  res,
}: {
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
  slug: string
}): Promise<ServerExperiments> {
  const expConfigs: ExperimentParamsArray = [
    { experimentKey: 'get-started-signup-page-cta', defaultValue: false },
    { experimentKey: 'show-pricing-cards-on-signup-pages', defaultValue: false },
    { experimentKey: 'sticky-cta-guild-signup-pages', defaultValue: 'control' },
    { experimentKey: 'selected-state-redesign-pricing-cards', defaultValue: 'control' },
    { experimentKey: 'guild-pricing-callout-extra-tickets-benefit', defaultValue: false },
    { experimentKey: 'guild-pricing-highlight-bonus-offer', defaultValue: false },
    { experimentKey: 'pricing-default-premium', defaultValue: false },
    { experimentKey: 'premium-callout', defaultValue: 'Most Popular' },
  ]

  const experiments = await runExperimentsOnServer(req, res, expConfigs)

  return experiments
}
